import {
  TriggerPayload,
  RepoOwners,
  RepositoryDetails,
  TriggerListItem,
} from '../types';

/**
 * Filters cloud build triggers based on repository name
 */
export function filterTriggersByRepo(
  allTriggers: TriggerPayload[],
  targetRepoName: string,
  repoOwners: RepoOwners,
): TriggerPayload[] {
  return allTriggers.filter(trigger => {
    // Case 1: Check GitHub property
    if (trigger?.github?.name === targetRepoName) {
      return true;
    }

    const repositoryPath = trigger?.repositoryEventConfig?.repository;
    if (!repositoryPath) {
      return false;
    }

    const pathParts = repositoryPath.split('/repositories/');
    const repoFullName = pathParts[pathParts.length - 1] || '';

    // Case 2: Check prefixed formats
    for (const owner of repoOwners) {
      const expectedFormat = `${owner}-${targetRepoName}`;
      if (repoFullName && repoFullName === expectedFormat) {
        return true;
      }
    }

    // Case 3: Direct name comparison
    return repoFullName === targetRepoName;
  });
}

/**
 * Extracts repository details from a cloud build trigger
 */
export function getRepositoryInfo(
  trigger: TriggerPayload,
  repoOwners: RepoOwners,
): RepositoryDetails {
  // Case 1: GitHub repository
  if (trigger.github?.owner && trigger.github?.name) {
    return {
      name: trigger.github.name,
      owner: trigger.github.owner,
      url: `https://github.com/${trigger.github.owner}/${trigger.github.name}`,
    };
  }

  // Case 2: Repository event configuration
  if (trigger.repositoryEventConfig?.repository) {
    const pathParts =
      trigger.repositoryEventConfig.repository.split('/repositories/');
    const repoFullName = pathParts[pathParts.length - 1];

    // Check all prefix formats
    for (const owner of repoOwners) {
      const prefix = `${owner}-`;
      if (repoFullName.startsWith(prefix)) {
        const actualRepoName = repoFullName.replace(prefix, '');
        return {
          name: actualRepoName,
          owner,
          url: `https://github.com/${owner}/${actualRepoName}`,
        };
      }
    }

    // Default repository event case
    return {
      name: repoFullName,
      owner: undefined,
      url: undefined,
    };
  }

  // Case 3: No repository information available
  return {
    name: 'No repository',
    owner: undefined,
    url: undefined,
  };
}

/**
 * Extract informations from resourceName for interacting with the sdk
 * 
 * @param resourceName projects/abc/locations/abc/triggers/abc
 * @returns projectId, locations, and triggers
 */
export const parseResourceName = (
  resourceName: string,
): { projectId: string; location: string; triggerId: string } | null => {
  const regex = /projects\/([^/]+)\/locations\/([^/]+)\/triggers\/([^/]+)/;
  const match = resourceName.match(regex);

  if (!match) {
    return null;
  }

  const [, projectId, location, triggerId] = match;
  return { projectId, location, triggerId };
};

const getTriggerEventFilterType = (trigger: TriggerPayload) => {
  if (
    trigger.github?.pullRequest !== undefined ||
    trigger.repositoryEventConfig?.pullRequest !== undefined
  ) {
    return 'pr';
  }
  return 'push';
};

export const transformTrigger: (
  trigger: TriggerPayload,
  prefixes: RepoOwners,
) => TriggerListItem = (trigger, prefixes) => {
  const parsedResource = trigger.resourceName
    ? parseResourceName(trigger.resourceName)
    : undefined;

  const repositoryInfo = getRepositoryInfo(trigger, prefixes);
  const eventFilterType = getTriggerEventFilterType(trigger);

  return {
    id: trigger.id,
    name: trigger.name,
    description: trigger.description || 'No description',
    repository: repositoryInfo,
    status: trigger.disabled ? 'DISABLED' : 'ENABLED',
    consoleUrl: parsedResource
      ? `https://console.cloud.google.com/cloud-build/triggers;region=${parsedResource.location}/edit/${parsedResource.triggerId}?project=${parsedResource.projectId}`
      : undefined,
    resourceName: trigger.resourceName,
    eventFilterType,
    trigger,
    tags: trigger.tags,
  };
};
